// NSRegularExpression patterns in JavaScript. ICU's `\b` is Unicode-aware (a word character is \w in ICU's sense:
// alphabetic, marks, decimal digits, connector punctuation, ZWJ/ZWNJ) where JavaScript's is ASCII-only even with the
// `u` flag, so `\b` is rewritten as lookarounds over that class. Everything else the ported patterns use means the same
// in both engines with the `u` flag.

const W = '[\\p{Alphabetic}\\p{M}\\p{Nd}\\p{Pc}\\u200C\\u200D]';
const BOUNDARY = `(?:(?<=${W})(?!${W})|(?<!${W})(?=${W}))`;

/** A pattern written for NSRegularExpression, compiled with the `u` flag (and `flags`) and ICU's word boundaries. */
export function icu(pattern: string, flags = ''): RegExp {
  // Replace `\b` (not an escaped backslash followed by b), outside character classes (none of the ported patterns
  // use `\b` inside one).
  const source = pattern.replace(/(\\\\)|\\b/g, (m, escaped) => (escaped ? m : BOUNDARY));
  return new RegExp(source, 'u' + flags);
}

/** `NSRegularExpression.escapedPattern(for:)`, for the `u` flag (only syntax characters may be escaped there). */
export function escapeRegExp(s: string): string {
  return s.replace(/[\\^$.*+?()[\]{}|/]/g, '\\$&');
}

/** Every match of `re` in `text` (from `start`, before `end`: the text outside is out of reach, as with
 *  NSRegularExpression's default opaque bounds), with UTF-16 offsets into `text`. */
export function matchesIn(re: RegExp, text: string, start = 0, end = text.length): RegExpExecArray[] {
  const slice = start === 0 && end === text.length ? text : text.slice(start, end);
  const g = new RegExp(re.source, re.flags.includes('g') ? re.flags : re.flags + 'g');
  const out: RegExpExecArray[] = [];
  let m: RegExpExecArray | null;
  while ((m = g.exec(slice)) !== null) {
    if (m[0] === '') g.lastIndex += 1;
    if (start) m.index += start;
    out.push(m);
  }
  return out;
}

/** The first match (or null), as `firstMatch(in:range:)`. */
export function firstMatchIn(re: RegExp, text: string, start = 0, end = text.length): RegExpExecArray | null {
  const slice = start === 0 && end === text.length ? text : text.slice(start, end);
  const g = new RegExp(re.source, re.flags.replace('g', ''));
  const m = g.exec(slice);
  if (m && start) m.index += start;
  return m;
}
